import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Brain,
  TrendingUp,
  Shield,
  Eye,
  Zap,
  FileText,
  Users,
  Target,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  Loader2,
  Calendar,
  Clock
} from "lucide-react";

import { blogAPI, contactAPI } from "../services/api";
import { useToast } from "../hooks/useToast";

const HomePage = () => {
  const [betaDialogOpen, setBetaDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [betaFormData, setBetaFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [scheduleFormData, setScheduleFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
  const [betaLoading, setBetaLoading] = useState(false);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [homepageBlogs, setHomepageBlogs] = useState([]);
  const [blogsLoading, setBlogsLoading] = useState(true);
  const { toast } = useToast();

  // Load homepage blogs on component mount
  useEffect(() => {
    const loadHomepageBlogs = async () => {
      try {
        setBlogsLoading(true);

        // Get selected blog IDs from localStorage
        const selectedBlogIds = JSON.parse(localStorage.getItem('myfinbrain_homepage_blogs') || '[]');

        if (selectedBlogIds.length === 0) {
          setHomepageBlogs([]);
          setBlogsLoading(false);
          return;
        }

        // Fetch all blogs
        const response = await blogAPI.getBlogs({ includeInternal: true, limit: 100 });

        if (response.success) {
          // Filter and order blogs according to selection
          const orderedBlogs = selectedBlogIds
            .map(id => response.data.blogs.find(blog => blog.id === id))
            .filter(blog => blog !== undefined); // Remove any blogs that weren't found

          setHomepageBlogs(orderedBlogs);
        }
      } catch (error) {
        console.error('Failed to load homepage blogs:', error);
        setHomepageBlogs([]);
      } finally {
        setBlogsLoading(false);
      }
    };

    loadHomepageBlogs();
  }, []);

  const handleBetaRequest = () => {
    setBetaDialogOpen(true);
  };

  const handleScheduleCall = () => {
    setScheduleDialogOpen(true);
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    setScheduleLoading(true);

    try {
      const requestData = {
        ...scheduleFormData,
        type: 'discovery'
      };

      const response = await contactAPI.submitRequest(requestData);

      if (response.success) {
        toast.success(response.data.message);
        setScheduleDialogOpen(false);
        setScheduleFormData({ name: "", email: "", company: "", message: "" });
      } else {
        toast.error(response.error.message);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleBetaFormSubmit = async (e) => {
    e.preventDefault();
    setBetaLoading(true);

    try {
      const requestData = {
        ...betaFormData,
        type: 'beta'
      };

      const response = await contactAPI.submitRequest(requestData);

      if (response.success) {
        toast.success(response.data.message);
        setBetaDialogOpen(false);
        setBetaFormData({ name: "", email: "", company: "", message: "" });
      } else {
        toast.error(response.error.message);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBetaLoading(false);
    }
  };

  const handleBetaFormChange = (field, value) => {
    setBetaFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 py-16 sm:py-20 lg:py-28 xl:py-32">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-slate-600/5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-slate-900 mb-6 sm:mb-8 leading-tight">
            <span className="block text-blue-600 whitespace-nowrap">Your Custom Second Brain</span>
            <span className="block text-slate-900">for Financial Data</span>
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-slate-800 mb-8 sm:mb-10 lg:mb-12 max-w-6xl mx-auto leading-tight font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            A New Paradigm for Actionable, Visualizable and Verifiable Qualitative Intelligence.
          </p>
          <p className="text-base sm:text-lg lg:text-xl text-slate-600 mb-10 sm:mb-12 max-w-5xl mx-auto leading-relaxed">
            MyFinBrain transforms unstructured financial documents into a dynamic second brain for you and your firm.
            <br className="hidden sm:block" />
            <span className="block sm:inline"> Uncover critical qualitative insights with the same rigor you apply to quantitative analysis, empowering investment decisions with unparalleled confidence.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={handleBetaRequest}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Request Beta Access
            </Button>
            <button
              onClick={() => document.getElementById('solutions')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-slate-600 hover:text-slate-900 flex items-center space-x-2 text-lg font-medium transition-colors duration-200"
            >
              <ChevronDown size={20} />
              <span>See How It Works</span>
            </button>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section id="challenges" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 md:flex md:flex-col md:items-start lg:block md:w-full lg:w-auto">
              Stop Drowning in Data. Start Discovering Insights.
            </h2>
            <p className="text-xl text-slate-600 max-w-4xl mx-auto">
              Financial teams face persistent challenges that legacy tools and generic AI can't solve.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <TrendingUp className="w-8 h-8 text-red-500" />,
                title: "Costly Data Digestion",
                description: "Manually analyzing dense reports, news, and memos is slow, expensive, and requires large specialist teams, creating a major operational drag."
              },
              {
                icon: <Eye className="w-8 h-8 text-orange-500" />,
                title: "The Qualitative Blind Spot",
                description: "Actionable qualitative insights are hard to systematically extract and track, yet are crucial for capturing business idiosyncrasies and a holistic investment view."
              },
              {
                icon: <Shield className="w-8 h-8 text-yellow-500" />,
                title: "Heavy Burden of Proof",
                description: "Every financial decision demands rigorous explainability and an auditable data trail. Integrating AI requires absolute transparency to meet compliance standards."
              },
              {
                icon: <Brain className="w-8 h-8 text-blue-500" />,
                title: "Limitations of Generic AI",
                description: "Generic AI tools and RAG chatbots are mostly textual / non-visual and miss deep finance context, leading to suboptimal analysis."
              }
            ].map((challenge, index) => (
              <Card key={index} className="border border-slate-200 hover:border-slate-300 transition-all duration-200 hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="mb-4">{challenge.icon}</div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">{challenge.title}</h3>
                  <p className="text-slate-600">{challenge.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solutions" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Introducing MyFinBrain: Your Custom Second Brain for Financial Data
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Our proprietary AI doesn't just read documents - it understands them and empowers you with unparalleled intelligence.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="max-w-2xl text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">Unlock New Capabilities</h3>
              <div className="space-y-6">
                {[
                  {
                    icon: <Eye className="w-6 h-6 text-blue-600" />,
                    title: "Visualize",
                    description: "Go beyond text. Intuitively map complex company structures, relationships,  and fundamental drivers in dynamic graphs."
                  },
                  {
                    icon: <Brain className="w-6 h-6 text-blue-600" />,
                    title: "Discover & Analyze",
                    description: "Systematically search, assess, and monitor qualitative factors with the same discipline as quantitative metrics."
                  },
                  {
                    icon: <FileText className="w-6 h-6 text-blue-600" />,
                    title: "Reproduce & Verify",
                    description: "Instantly generate templated investment memos, credit reports, and investor updates, pre-populated with data and auditable, click-through citations."
                  }
                ].map((capability, index) => (
                  <div key={index} className="flex flex-col items-center text-center space-y-2">
                    <div className="flex-shrink-0">{capability.icon}</div>
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-1">{capability.title}</h4>
                      <p className="text-slate-600">{capability.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Enhance Your Tools. Don't Replace Them.
            </h2>
            <p className="text-xl text-slate-600 max-w-4xl mx-auto">
              MyFinBrain is designed to become an indispensable, intelligent layer within your existing operations.
              We deliver our insights through robust APIs and direct data feeds, ensuring our intelligence flows
              directly into the systems your team uses every day.
            </p>
          </div>

          <div className="mb-16">
            <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Empowering Your Entire Team</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Users className="w-8 h-8 text-blue-600" />,
                  title: "For Investment Researchers",
                  description: "Enrich your internal research platforms with real-time qualitative insights, accelerating memo generation and deepening your analysis."
                },
                {
                  icon: <CheckCircle className="w-8 h-8 text-blue-600" />,
                  title: "For Due Diligence Officers",
                  description: "Streamline your review process by auto-populating checklists and CRMs with verified risk factors, contractual obligations, and regulatory exposures."
                },
                {
                  icon: <Shield className="w-8 h-8 text-blue-600" />,
                  title: "For Risk Managers",
                  description: "Feed real-time qualitative risk alerts—from covenant breaches to sentiment shifts—directly into your risk dashboards for proactive vigilance."
                }
              ].map((role, index) => (
                <Card key={index} className="border border-slate-200 hover:border-blue-200 transition-all duration-200 hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="mb-4">{role.icon}</div>
                    <h4 className="text-lg font-semibold text-slate-900 mb-3">{role.title}</h4>
                    <p className="text-slate-600">{role.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold text-slate-900 mb-8">Ready to Transform Your Enterprise Systems?</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={handleBetaRequest}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Request Beta Access
              </Button>
              <button
                onClick={handleScheduleCall}
                className="text-blue-600 hover:text-blue-700 flex items-center space-x-2 text-lg font-medium transition-colors duration-200"
              >
                <span>Schedule a Discovery Call</span>
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why MyFinBrain Section */}
      <section id="why-us" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Systematic Fundamental Insight & Unmatched Trust
            </h2>
          </div>

          {/* Two cards in top row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 max-w-4xl mx-auto">
            {[
              {
                icon: <Target className="w-8 h-8 text-blue-600" />,
                title: "Pioneer Systematic Qualitative Analysis",
                description: "Apply the same rigor to qualitative risk factors (e.g., supply chain resilience, management changes, geopolitical exposure) as to quantitative data, enabling a truly holistic portfolio view."
              },
              {
                icon: <Brain className="w-8 h-8 text-blue-600" />,
                title: "Superior AI Chat built for Financial Context",
                description: "Our AI is built for finance. It goes beyond simple text search to understand deep context, delivering precise, actionable answers that generic tools miss."
              }
            ].map((feature, index) => (
              <Card key={index} className="border border-slate-200 hover:border-blue-200 transition-all duration-200 hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Three cards in bottom row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="w-8 h-8 text-blue-600" />,
                title: "Enhanced Explainability & Auditability",
                description: "We provide inherent model explainability, human oversight, and an immutable audit trail for every insight, ensuring compliance and confidence far beyond simple citations."
              },
              {
                icon: <Eye className="w-8 h-8 text-blue-600" />,
                title: "Intuitive Knowledge Visualization",
                description: "Move beyond flat text. Explore complex entity relationships in dynamic graph formats and key facts in concise bulleted lists for rapid, deeper understanding."
              },
              {
                icon: <Zap className="w-8 h-8 text-blue-600" />,
                title: "Seamless & Secure Integration",
                description: "Designed for easy integration into enterprise workflows, MyFinBrain becomes a natural extension of your workflows. We adhere to the highest standards of data security and privacy to protect your sensitive financial information."
              }
            ].map((feature, index) => (
              <Card key={index} className="border border-slate-200 hover:border-blue-200 transition-all duration-200 hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>





      {/* Beta Program & CTA Section */}
      <section id="beta-program" className="py-20 bg-gradient-to-br from-blue-900 via-slate-800 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Partner With Us. Shape the Future of Financial Intelligence.
          </h2>
          <p className="text-xl text-blue-100 mb-12 max-w-4xl mx-auto">
            MyFinBrain is inviting a select group of institutional partners to join our exclusive Beta Program. 
            This is a unique opportunity to gain a competitive edge and directly influence a transformative AI platform 
            built for the complexities of modern finance.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              "Gain early access to our transformative AI",
              "Directly shape our product roadmap to meet your needs",
              "Receive dedicated, priority support from our core team",
              "Pilot custom integrations for your enterprise workflows"
            ].map((benefit, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                <CheckCircle className="w-6 h-6 text-blue-300 mx-auto mb-3" />
                <p className="text-white">{benefit}</p>
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-2xl font-bold mb-8">Ready to Redefine Your Investment Strategy?</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                onClick={handleBetaRequest}
                data-beta-request-button
                className="bg-white text-blue-900 hover:bg-blue-50 px-8 py-4 text-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Request Beta Access
              </Button>
              <button 
                onClick={handleScheduleCall}
                className="text-white hover:text-blue-200 flex items-center space-x-2 text-lg font-medium transition-colors duration-200"
              >
                <span>Schedule a Discovery Call</span>
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      {(homepageBlogs.length > 0 || blogsLoading) && (
        <section id="blog" className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Insights & Expertise
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Stay informed with the latest insights on AI-powered financial intelligence,
                industry trends, and best practices from our team of experts.
              </p>
            </div>

            {blogsLoading ? (
              <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <span className="ml-2 text-slate-600">Loading blog posts...</span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  {homepageBlogs.map((post, index) => (
                    <Card key={index} className="border border-slate-200 hover:border-blue-200 transition-all duration-200 hover:shadow-lg group">
                      <CardContent className="p-6">
                        <div className="mb-4">
                          <span className="inline-block bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                            {post.category}
                          </span>
                        </div>
                        <h3 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors duration-200">
                          {post.title}
                        </h3>
                        <p className="text-slate-600 mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-sm text-slate-500">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{post.publishedAt}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>{post.readTime}</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="text-center">
                  <Link to="/blog">
                    <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 transition-all duration-200 shadow-md hover:shadow-lg">
                      View All Articles
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-slate-900 mb-8">Get In Touch</h2>
          <p className="text-xl text-slate-600 mb-8">
            Ready to transform your financial intelligence? Let's start the conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <a
              href="mailto:hello@myfinbrain.com"
              className="text-blue-600 hover:text-blue-700 text-lg font-medium transition-colors duration-200"
            >
              hello@myfinbrain.com
            </a>
            <Button 
              onClick={handleBetaRequest}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Join Our Beta Program
            </Button>
          </div>
        </div>
      </section>

      {/* Beta Request Dialog */}
      <Dialog open={betaDialogOpen} onOpenChange={setBetaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Beta Access</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBetaFormSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Full Name *
              </label>
              <Input
                type="text"
                value={betaFormData.name}
                onChange={(e) => handleBetaFormChange("name", e.target.value)}
                placeholder="Enter your full name"
                required
                disabled={betaLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email Address *
              </label>
              <Input
                type="email"
                value={betaFormData.email}
                onChange={(e) => handleBetaFormChange("email", e.target.value)}
                placeholder="Enter your email address"
                required
                disabled={betaLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Company *
              </label>
              <Input
                type="text"
                value={betaFormData.company}
                onChange={(e) => handleBetaFormChange("company", e.target.value)}
                placeholder="Enter your company name"
                required
                disabled={betaLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Message (Optional)
              </label>
              <Textarea
                value={betaFormData.message}
                onChange={(e) => handleBetaFormChange("message", e.target.value)}
                placeholder="Tell us about your use case or specific needs..."
                rows={3}
                disabled={betaLoading}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setBetaDialogOpen(false)}
                className="flex-1"
                disabled={betaLoading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={betaLoading}
              >
                {betaLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit Request"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Discovery Call Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule a Discovery Call</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleScheduleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Full Name *
              </label>
              <Input
                type="text"
                value={scheduleFormData.name}
                onChange={(e) => setScheduleFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter your full name"
                required
                disabled={scheduleLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email Address *
              </label>
              <Input
                type="email"
                value={scheduleFormData.email}
                onChange={(e) => setScheduleFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter your email address"
                required
                disabled={scheduleLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Company *
              </label>
              <Input
                type="text"
                value={scheduleFormData.company}
                onChange={(e) => setScheduleFormData(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Enter your company name"
                required
                disabled={scheduleLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Message (Optional)
              </label>
              <Textarea
                value={scheduleFormData.message}
                onChange={(e) => setScheduleFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder="Tell us about your goals for the discovery call..."
                rows={3}
                disabled={scheduleLoading}
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setScheduleDialogOpen(false)}
                className="flex-1"
                disabled={scheduleLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                disabled={scheduleLoading}
              >
                {scheduleLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Schedule Call"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomePage;